import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, QrCode, Download, RefreshCw, ChevronDown } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { api, unwrap, API_BASE, getToken } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const DESTINATIONS = [
  { value: "home", label: "Home (evercleanhome.online)" },
  { value: "book", label: "Book a cleaning (/book)" },
  { value: "work_with_us", label: "Work with us / Apply as cleaner (/work-with-us)" },
  { value: "track", label: "Track a booking (/track/{booking_number})" },
  { value: "custom", label: "Custom URL" },
];
const DEST_LABEL = Object.fromEntries(DESTINATIONS.map((d) => [d.value, d.label]));
const SIZE_LABEL = { small: "Small (300×300px)", medium: "Medium (600×600px)", large: "Large (1200×1200px)" };

export default function AdminQrGenerator() {
  const [destination, setDestination] = useState("book");
  const [bookingNumber, setBookingNumber] = useState("");
  const [customUrl, setCustomUrl] = useState("");
  const [includeLogo, setIncludeLogo] = useState(true);
  const [color, setColor] = useState("brand");
  const [size, setSize] = useState("medium");
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [previewUrl, setPreviewUrl] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const [bookingResults, setBookingResults] = useState([]);
  const [showBookingDropdown, setShowBookingDropdown] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const debounceRef = useRef(null);
  const bookingDebounceRef = useRef(null);
  const objectUrlRef = useRef(null);

  const buildPayload = useCallback(
    (extra = {}) => ({
      destination,
      booking_number: bookingNumber,
      custom_url: customUrl,
      include_logo: includeLogo,
      color,
      format: "png",
      size,
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      save_history: false,
      ...extra,
    }),
    [destination, bookingNumber, customUrl, includeLogo, color, size, utmSource, utmMedium, utmCampaign]
  );

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get("/admin/qr/history");
      setHistory(unwrap(r)?.items || []);
    } catch {
      // silent — non-critical widget
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Live preview — debounced, never persisted to history.
  useEffect(() => {
    if (destination === "track" && !bookingNumber.trim()) {
      setPreviewUrl("");
      setPreviewError("");
      return;
    }
    if (destination === "custom" && !customUrl.trim()) {
      setPreviewUrl("");
      setPreviewError("");
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError("");
      try {
        const res = await fetch(`${API_BASE}/admin/qr/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify(buildPayload()),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.detail?.message || "Could not generate preview");
        }
        const blob = await res.blob();
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setPreviewUrl(url);
      } catch (err) {
        setPreviewUrl("");
        setPreviewError(err.message || "Could not generate preview");
      } finally {
        setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination, bookingNumber, customUrl, includeLogo, color, size, utmSource, utmMedium, utmCampaign]);

  // Booking number typeahead for the "Track a booking" destination.
  useEffect(() => {
    if (destination !== "track" || !bookingNumber.trim()) {
      setBookingResults([]);
      return;
    }
    if (bookingDebounceRef.current) clearTimeout(bookingDebounceRef.current);
    bookingDebounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get("/admin/bookings", { params: { search: bookingNumber, page_size: 6 } });
        setBookingResults(unwrap(r)?.items || []);
      } catch {
        setBookingResults([]);
      }
    }, 300);
    return () => clearTimeout(bookingDebounceRef.current);
  }, [bookingNumber, destination]);

  const download = async (format, dlSize) => {
    setDownloading(true);
    try {
      const payload = buildPayload({ format, size: dlSize || size, save_history: true });
      const res = await fetch(`${API_BASE}/admin/qr/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail?.message || "Could not generate file");
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `everclean-qr-${destination}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Downloaded ${format.toUpperCase()}`);
      loadHistory();
    } catch (err) {
      toast.error(err.message || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const regenerate = (item) => {
    setDestination(item.destination);
    setBookingNumber(item.booking_number || "");
    setCustomUrl(item.custom_url || "");
    setIncludeLogo(!!item.include_logo);
    setColor(item.color || "brand");
    setSize(item.size || "medium");
    setUtmSource(item.utm_source || "");
    setUtmMedium(item.utm_medium || "");
    setUtmCampaign(item.utm_campaign || "");
    setAdvancedOpen(!!(item.utm_source || item.utm_medium || item.utm_campaign));
    toast.success("Loaded into the generator — adjust and download again");
  };

  const previewTarget =
    destination === "track" ? (bookingNumber ? `/track/${bookingNumber}` : "—") :
    destination === "custom" ? (customUrl || "—") :
    DEST_LABEL[destination];

  return (
    <div className="space-y-6" data-testid="admin-qr-generator">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter">QR Code Generator</h1>
        <p className="text-[#4B5563] text-sm mt-1">Generate branded QR codes for marketing, recruiting, or tracking bookings</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start">
        {/* LEFT — form */}
        <Card className="rounded-2xl border border-[#E5E7EB] p-6 space-y-5" data-testid="qr-form-card">
          <div className="space-y-2">
            <Label>Destination</Label>
            <Select value={destination} onValueChange={setDestination}>
              <SelectTrigger className="h-11 rounded-xl" data-testid="qr-destination-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DESTINATIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value} data-testid={`qr-destination-option-${d.value}`}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {destination === "track" && (
            <div className="space-y-2 relative">
              <Label>Booking number</Label>
              <Input
                value={bookingNumber}
                onChange={(e) => setBookingNumber(e.target.value)}
                onFocus={() => setShowBookingDropdown(true)}
                onBlur={() => setTimeout(() => setShowBookingDropdown(false), 150)}
                placeholder="e.g. EC-2026-00149"
                className="h-11 rounded-xl font-mono"
                data-testid="qr-booking-number-input"
              />
              {showBookingDropdown && bookingResults.length > 0 && (
                <div
                  className="absolute z-10 mt-1 w-full bg-white border border-[#E5E7EB] rounded-xl shadow-lg overflow-hidden max-h-56 overflow-y-auto"
                  data-testid="qr-booking-dropdown"
                >
                  {bookingResults.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      onMouseDown={() => { setBookingNumber(b.booking_number); setShowBookingDropdown(false); }}
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F5F5F4] flex items-center justify-between gap-2"
                      data-testid={`qr-booking-option-${b.booking_number}`}
                    >
                      <span className="font-mono font-medium whitespace-nowrap">{b.booking_number}</span>
                      <span className="text-xs text-[#9CA3AF] truncate">{b.customer?.full_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {destination === "custom" && (
            <div className="space-y-2">
              <Label>Custom URL</Label>
              <Input
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                placeholder="https://..."
                className="h-11 rounded-xl"
                data-testid="qr-custom-url-input"
              />
            </div>
          )}

          <div className="flex items-center justify-between py-1">
            <div>
              <Label className="text-sm">Include logo</Label>
              <p className="text-xs text-[#9CA3AF] mt-0.5">Overlay the Ever Clean Home brandmark at the center</p>
            </div>
            <Switch checked={includeLogo} onCheckedChange={setIncludeLogo} data-testid="qr-include-logo-toggle" />
          </div>

          <div className="space-y-2">
            <Label>Color</Label>
            <RadioGroup value={color} onValueChange={setColor} className="flex gap-5" data-testid="qr-color-radio">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <RadioGroupItem value="brand" data-testid="qr-color-brand" /> Brand navy (default)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <RadioGroupItem value="black" data-testid="qr-color-black" /> Black on white
              </label>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label>Size preview</Label>
            <Select value={size} onValueChange={setSize}>
              <SelectTrigger className="h-11 rounded-xl w-full sm:w-56" data-testid="qr-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SIZE_LABEL).map(([value, label]) => (
                  <SelectItem key={value} value={value} data-testid={`qr-size-option-${value}`}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger asChild>
              <button type="button" className="flex items-center gap-1.5 text-sm font-semibold text-[#0F766E]" data-testid="qr-advanced-toggle">
                <ChevronDown className={`w-4 h-4 transition-transform ${advancedOpen ? "rotate-180" : ""}`} />
                Advanced — UTM tracking params
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">UTM source</Label>
                  <Input value={utmSource} onChange={(e) => setUtmSource(e.target.value)} placeholder="cartel_boston_north" className="h-10 rounded-lg text-sm" data-testid="qr-utm-source-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">UTM medium</Label>
                  <Input value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder="qr" className="h-10 rounded-lg text-sm" data-testid="qr-utm-medium-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">UTM campaign</Label>
                  <Input value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} placeholder="lanzamiento_julio2026" className="h-10 rounded-lg text-sm" data-testid="qr-utm-campaign-input" />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className="flex flex-wrap gap-3 pt-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button disabled={downloading || !previewUrl} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="qr-download-png-btn">
                  {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Download PNG <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {Object.entries(SIZE_LABEL).map(([value, label]) => (
                  <DropdownMenuItem key={value} onClick={() => download("png", value)} data-testid={`qr-download-png-${value}`}>
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              disabled={downloading || !previewUrl}
              onClick={() => download("pdf")}
              className="rounded-xl"
              data-testid="qr-download-pdf-btn"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download PDF
            </Button>
          </div>
        </Card>

        {/* RIGHT — live preview */}
        <Card className="rounded-2xl border border-[#E5E7EB] p-6 flex flex-col items-center gap-4 lg:sticky lg:top-6" data-testid="qr-preview-card">
          <div className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold self-start">Live preview</div>
          <div className="w-56 h-56 rounded-2xl border border-[#E5E7EB] bg-[#FAFAF9] grid place-items-center overflow-hidden" data-testid="qr-preview-box">
            {previewLoading ? (
              <Loader2 className="w-6 h-6 animate-spin text-[#9CA3AF]" />
            ) : previewError ? (
              <p className="text-xs text-red-500 text-center px-4" data-testid="qr-preview-error">{previewError}</p>
            ) : previewUrl ? (
              <img src={previewUrl} alt="QR preview" className="w-full h-full object-contain p-2" data-testid="qr-preview-image" />
            ) : (
              <div className="text-center px-4">
                <QrCode className="w-8 h-8 mx-auto text-[#D1D5DB] mb-2" />
                <p className="text-xs text-[#9CA3AF]">Fill in the required field to preview</p>
              </div>
            )}
          </div>
          <p className="text-xs text-[#9CA3AF] text-center break-all max-w-[240px]" data-testid="qr-preview-target-url">{previewTarget}</p>
        </Card>
      </div>

      {/* History */}
      <Card className="rounded-2xl border border-[#E5E7EB] overflow-hidden" data-testid="qr-history-card">
        <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">Recent QR codes</h2>
          <Button variant="ghost" size="sm" onClick={loadHistory} className="rounded-xl" data-testid="qr-history-refresh-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
        {historyLoading ? (
          <div className="py-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : history.length === 0 ? (
          <div className="py-10 text-center text-sm text-[#9CA3AF]" data-testid="qr-history-empty">No QR codes generated yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="qr-history-table">
              <thead>
                <tr className="bg-[#FAFAF9] text-left text-xs uppercase tracking-wider text-[#6B7280]">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Destination</th>
                  <th className="px-4 py-3 font-semibold">UTM</th>
                  <th className="px-4 py-3 font-semibold" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {history.map((h, i) => (
                  <tr key={i} className="hover:bg-[#FAFAF9]" data-testid={`qr-history-row-${i}`}>
                    <td className="px-4 py-3 text-xs text-[#9CA3AF] font-mono whitespace-nowrap">{formatDateTime(h.created_at)}</td>
                    <td className="px-4 py-3">
                      <span className="font-medium">{DEST_LABEL[h.destination] || h.destination}</span>
                      {h.destination === "track" && h.booking_number && (
                        <span className="text-xs text-[#9CA3AF] ml-1 font-mono">({h.booking_number})</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#4B5563]">
                      {h.utm_source || h.utm_medium || h.utm_campaign
                        ? [h.utm_source, h.utm_medium, h.utm_campaign].filter(Boolean).join(" / ")
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="outline" size="sm" onClick={() => regenerate(h)} className="rounded-lg text-xs" data-testid={`qr-history-regenerate-${i}`}>
                        Regenerate
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
